import dayjs from "dayjs";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";

import { useUpdateRaidTodoSort } from "@core/hooks/mutations/todo";
import { useSchedulesMonth } from "@core/hooks/queries/schedule";
import { updateCharacterQueryData } from "@core/lib/queryClient";
import type { Character, TodoRaid } from "@core/types/character";
import type { Friend } from "@core/types/friend";

import BoxTitle from "@components/BoxTitle";
import Button from "@components/Button";

import CharacterRaidProfit from "./CharacterRaidProfit";
import EditModal from "./EditModal";
import RaidItem from "./RaidItem";
import RaidSortWrap from "./RaidSortWrap";

// 레이드명에서 기본 이름만 추출 (난이도/단계/숫자 제거)
const getBaseRaidName = (name: string) => {
  return name
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter((part) => !/^\d+$/.test(part) && !/^\d+단계$/.test(part))
    .filter(
      (part) =>
        !["하드", "노말", "도전", "싱글", "익스트림", "에픽", "헬", "시련"].includes(part)
    )
    .join(" ");
};

export interface Props {
  character: Character;
  friend?: Friend;
}

const RAID_ACCORDION_KEY = "raid-accordion-state";

const getStoredRaidExpanded = (characterId: number): boolean => {
  try {
    const stored = localStorage.getItem(RAID_ACCORDION_KEY);
    if (stored) {
      const map: Record<string, boolean> = JSON.parse(stored);
      return map[characterId] ?? true;
    }
  } catch { /* ignore */ }
  return true;
};

const setStoredRaidExpanded = (characterId: number, value: boolean) => {
  try {
    const stored = localStorage.getItem(RAID_ACCORDION_KEY);
    const map: Record<string, boolean> = stored ? JSON.parse(stored) : {};
    map[characterId] = value;
    localStorage.setItem(RAID_ACCORDION_KEY, JSON.stringify(map));
  } catch { /* ignore */ }
};

const TodoWeekRaid: FC<Props> = ({ character, friend }) => {
  const [expanded, setExpanded] = useState(() =>
    getStoredRaidExpanded(character.characterId)
  );
  const [editModal, setEditModal] = useState(false);
  const [sortMode, setSortMode] = useState(false);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      setStoredRaidExpanded(character.characterId, next);
      return next;
    });
  }, [character.characterId]);
  const [sortedWeeklyRaidTodoList, setSortedWeeklyRaidTodoList] =
    useState<TodoRaid[]>();

  const updateRaidTodoSort = useUpdateRaidTodoSort({
    onSuccess: (character, { friendUsername }) => {
      updateCharacterQueryData({
        character,
        friendUsername,
      });

      toast.success("레이드 순서 업데이트가 완료되었습니다.");
      setSortMode(false);
    },
  });

  const today = useMemo(() => dayjs(), []);
  const startDate = useMemo(() => today.startOf("month"), [today]);

  const getSchedules = useSchedulesMonth({
    year: startDate.year(),
    month: startDate.month() + 1,
  });

  // 이번 주 수요일 ~ 다음 주 화요일 범위 계산
  const startOfWeek = today.day() >= 3 ? today.day(3) : today.day(-4);
  const endOfWeek = startOfWeek.add(6, "day");

  const filteredSchedules = useMemo(() => {
    if (!getSchedules.data) return [];

    return getSchedules.data.filter((schedule) => {
      if (schedule.scheduleRaidCategory !== "RAID") return false;
      if (schedule.repeatWeek) return true;
      if (!schedule.date) return false;

      const scheduleDate = dayjs(schedule.date);
      return (
        scheduleDate.isSameOrAfter(startOfWeek, "day") &&
        scheduleDate.isSameOrBefore(endOfWeek, "day")
      );
    });
  }, [getSchedules.data, startOfWeek, endOfWeek]);

  // character.todoList 순서대로 초기화
  useEffect(() => {
    setSortedWeeklyRaidTodoList([...character.todoList]);
  }, [sortMode]);

  // 스케줄을 Map으로 캐싱 (기본 레이드명으로 매칭)
  const scheduleMap = useMemo(() => {
    const map = new Map<string, (typeof filteredSchedules)[number]>();

    filteredSchedules.forEach((schedule) => {
      if (!schedule.autoCheck) return;
      const key = `${schedule.characterName}|${getBaseRaidName(schedule.raidName)}`;
      map.set(key, schedule);
    });

    return map;
  }, [filteredSchedules]);

  return (
    <>
      <Wrapper>
        <TitleBox>
          <TitleRow>
            <BoxTitle>주간 레이드</BoxTitle>
            <ButtonsBox>
              {sortMode ? (
                sortedWeeklyRaidTodoList && (
                  <Button
                    size="small"
                    onClick={() =>
                      updateRaidTodoSort.mutate({
                        friendUsername: friend?.friendUsername,
                        characterId: character.characterId,
                        sorted: sortedWeeklyRaidTodoList,
                      })
                    }
                  >
                    저장
                  </Button>
                )
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSortMode(true)}
                >
                  정렬
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={() => setEditModal(true)}
              >
                편집
              </Button>
              <FoldButton onClick={toggleExpanded}>
                <ToggleArrow $expanded={expanded}>▼</ToggleArrow>
              </FoldButton>
            </ButtonsBox>
          </TitleRow>

          {sortMode ? (
            <SubTitle>저장 버튼 클릭시 순서가 저장됩니다</SubTitle>
          ) : (
            <CharacterRaidProfit character={character} />
          )}
        </TitleBox>

        <CollapsiblePanel $expanded={expanded}>
          <CollapsibleInner>
            {sortMode
              ? sortedWeeklyRaidTodoList && (
                  <RaidSortWrap
                    character={character}
                    friend={friend}
                    todoList={sortedWeeklyRaidTodoList}
                    setTodos={(newTodoList) =>
                      setSortedWeeklyRaidTodoList(newTodoList)
                    }
                  />
                )
              : character.todoList.map((todo) => {
                  const baseName = getBaseRaidName(todo.name);
                  const key = `${character.characterName}|${baseName}`;
                  const matchingSchedule = scheduleMap.get(key);

                  return (
                    <RaidItem
                      key={todo.id}
                      todo={todo}
                      character={character}
                      friend={friend}
                      schedule={matchingSchedule}
                    />
                  );
                })}
          </CollapsibleInner>
        </CollapsiblePanel>
      </Wrapper>

      <EditModal
        onClose={() => setEditModal(false)}
        isOpen={editModal}
        character={character}
        friend={friend}
      />
    </>
  );
};

export default TodoWeekRaid;

export const Wrapper = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.app.bg.white};
`;

const TitleBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 5px 10px;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-width: 0;
  flex-wrap: wrap; /* [overflow-fix] 카드 폭 좁을 때 정렬/편집 버튼이 다음 줄로 */
  column-gap: 4px;
  row-gap: 3px;

  ${({ theme }) => theme.medias.max900} {
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
  }
`;

const ButtonsBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  flex-shrink: 0; /* [overflow-fix] 제목이 우측 버튼을 축소시키지 않도록 */
`;

const FoldButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const ToggleArrow = styled.span<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  font-size: 10px;
  color: ${({ theme }) => theme.app.text.light2};
  transition: transform 0.2s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? "180deg" : "0deg")});
`;

const CollapsiblePanel = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $expanded }) => ($expanded ? "1fr" : "0fr")};
  transition: grid-template-rows 0.25s ease;
`;

const CollapsibleInner = styled.div`
  overflow: hidden;
  min-height: 0;
`;

const SubTitle = styled.p`
  color: ${({ theme }) => theme.app.text.dark2};
  font-size: 12px;
`;
